/**
 * The printed board — Style Bible §0, §2.
 *
 * A board space on a 1980s game board is a shape printed on card: a flat spot-ink
 * fill, a heavy black key line, a symbol, and a number. It is not a 3D tile with
 * lighting, because the board is paper.
 */

import {
  HALFTONE,
  INK,
  LINE,
  MISREGISTRATION,
  PAPER,
  PAPER_RADIUS,
  ROLE_INK,
  SPOT,
  regionInk,
  type RegionId,
} from './tokens.js';
import {
  agedEdges,
  boardFold,
  circle,
  defs,
  group,
  halftonePattern,
  misregistered,
  n,
  paperGrain,
  path,
  roundedRectPath,
  svg,
  tag,
} from './svg.js';
import type { RoomKind } from './tile.js';

/** How the player currently relates to a space. */
export type RoomState = 'fogged' | 'known' | 'visited' | 'cleared' | 'current' | 'reachable';

/** One spot ink and one Korean label per space type, fixed so the board is learnable. */
export const KIND_STYLE: Readonly<Record<RoomKind, { color: string; label: string }>> = {
  entrance: { color: SPOT.yellow, label: '출발' },
  combat: { color: SPOT.red, label: '전투' },
  npc: { color: SPOT.green, label: '인물' },
  treasure: { color: SPOT.yellow, label: '보물' },
  trap: { color: SPOT.orange, label: '함정' },
  shop: { color: SPOT.green, label: '상점' },
  puzzle: { color: SPOT.blue, label: '수수께끼' },
  event: { color: SPOT.blue, label: '사건' },
  secret: { color: SPOT.purple, label: '비밀' },
  boss: { color: SPOT.orange, label: '보스' },
};

/**
 * Space symbols, drawn in a 64×64 box. Printed board symbols are bold and
 * simple — they have to survive being 8mm across on card.
 */
const KIND_GLYPH: Readonly<Record<RoomKind, string>> = {
  entrance: 'M16 46 h10 v-9 h10 v-9 h12 v-8 M44 20 h8 v8',
  combat: 'M18 18 L46 46 M46 18 L18 46',
  npc: 'M32 15 a7 7 0 1 1 -0.1 0 M32 23 v15 M21 29 h22 M25 51 l7 -13 l7 13',
  treasure: 'M13 27 h38 v23 h-38 Z M13 27 a19 10 0 0 1 38 0 M29 34 h6 v9 h-6 Z',
  trap: 'M11 47 h42 M15 47 l6 -16 l6 16 l6 -16 l6 16 l6 -16 l6 16',
  shop: 'M32 13 v35 M17 21 h30 M17 21 l-6 13 h12 Z M47 21 l-6 13 h12 Z',
  puzzle: 'M24 32 a10 10 0 1 1 0.1 0 M40 32 a10 10 0 1 1 0.1 0',
  event: 'M32 13 L51 32 L32 51 L13 32 Z M32 23 v13 M32 42 v3',
  secret: 'M32 19 a8 8 0 1 1 0.1 0 M26 33 h12 l4 15 h-20 Z',
  boss: 'M13 47 L18 21 L26 34 L32 17 L38 34 L46 21 L51 47 Z',
};

export interface MapTileOptions {
  readonly kind: RoomKind;
  readonly state: RoomState;
  readonly size?: number;
  readonly region?: RegionId;
  readonly seed?: string;
  readonly cost?: number;
}

/**
 * One printed space.
 *
 * Rounded square on card, spot ink misregistered from the key line, symbol on
 * top. Unknown spaces print as a plain "?" panel — the board was printed before
 * you ever played it, so the space exists; you just have not been there.
 */
export function renderMapTile(opts: MapTileOptions): string {
  const size = opts.size ?? 120;
  const { kind, state } = opts;
  const seed = opts.seed ?? kind;
  const s = size / 120;
  const ink = KIND_STYLE[kind].color;

  const inset = size * 0.07;
  const box = size - inset * 2;
  const radius = box * 0.17;
  const shape = roundedRectPath(inset, inset, box, box, radius);

  const fogged = state === 'fogged';
  const cleared = state === 'cleared';

  const screenId = `mscreen-${kind}-${state}`;
  const head = defs(
    halftonePattern(screenId, fogged ? INK.faded : ink, HALFTONE.mid.dot, HALFTONE.mid.grid),
  );

  // Card under the space, so every space sits on the board's paper.
  const card = path(shape, { fill: PAPER.card });

  // Spot ink: solid for the space you are on, a screen for everything else, so
  // "where am I" reads instantly without adding a colour.
  const fill = fogged
    ? path(shape, { fill: `url(#${screenId})`, opacity: 0.5 })
    : misregistered(
        `tile:${seed}`,
        size,
        MISREGISTRATION,
        state === 'current'
          ? path(shape, { fill: ink })
          : path(shape, { fill: `url(#${screenId})` }),
      );

  const keyline = path(shape, {
    fill: 'none',
    stroke: INK.key,
    'stroke-width': LINE.outline * s,
    'stroke-linejoin': 'round',
  });

  if (fogged) {
    return svg(
      size,
      size,
      head +
        card +
        fill +
        keyline +
        tag(
          'text',
          {
            x: size / 2,
            y: size * 0.63,
            'text-anchor': 'middle',
            'font-family': 'Noto Serif KR, Georgia, serif',
            'font-size': 34 * s,
            'font-weight': 700,
            fill: INK.faded,
          },
          '?',
        ),
    );
  }

  const glyphScale = (size * 0.48) / 64;
  const glyph = group(
    {
      transform: `translate(${n(size / 2 - (64 * glyphScale) / 2)} ${n(size * 0.44 - (64 * glyphScale) / 2)}) scale(${n(glyphScale)})`,
      opacity: cleared ? 0.35 : 1,
    },
    path(KIND_GLYPH[kind], {
      fill: 'none',
      stroke: state === 'current' ? PAPER.card : INK.key,
      'stroke-width': 5.5,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    }),
  );

  // Cleared spaces get a printed tick, the way you would pencil one in.
  const clearedMark = cleared
    ? path(
        `M${n(size * 0.3)} ${n(size * 0.52)} l${n(size * 0.11)} ${n(size * 0.13)} l${n(size * 0.28)} ${n(size * 0.32 * -1)}`,
        {
          fill: 'none',
          stroke: SPOT.green,
          'stroke-width': LINE.outline * s * 1.3,
          'stroke-linecap': 'round',
        },
      )
    : '';

  // Reachable: a printed cost token clipped to the corner of the space.
  const reach =
    state === 'reachable' && opts.cost !== undefined
      ? circle({
          cx: size - inset - size * 0.02,
          cy: inset + size * 0.02,
          r: 15 * s,
          fill: SPOT.yellow,
          stroke: INK.key,
          'stroke-width': LINE.structure * s,
        }) +
        tag(
          'text',
          {
            x: size - inset - size * 0.02,
            y: inset + size * 0.02 + 6 * s,
            'text-anchor': 'middle',
            'font-family': 'Noto Serif KR, Georgia, serif',
            'font-size': 19 * s,
            'font-weight': 700,
            fill: INK.key,
          },
          String(opts.cost),
        )
      : '';

  return svg(size, size, head + card + fill + keyline + glyph + clearedMark + reach);
}

/**
 * The board sheet itself: cardstock, grain, yellowed edges, and the fold.
 * Rendered behind the spaces by the client.
 */
export function renderBoardSheet(
  width: number,
  height: number,
  region: RegionId = 'aurelia',
  seed = 'board',
): string {
  const grainId = 'board-grain';
  const screenId = 'board-screen';
  const ink = regionInk(region);

  const head = defs(
    paperGrain(grainId, INK.key, seed) +
      halftonePattern(screenId, ink, HALFTONE.light.dot, HALFTONE.light.grid),
  );

  const sheet =
    path(roundedRectPath(0, 0, width, height, PAPER_RADIUS), { fill: PAPER.board }) +
    // A faint regional screen, so a board in the forest is not the same sheet as
    // one in the desert without needing a second palette.
    path(roundedRectPath(0, 0, width, height, PAPER_RADIUS), { fill: `url(#${screenId})`, opacity: 0.5 }) +
    path(roundedRectPath(0, 0, width, height, PAPER_RADIUS), { fill: `url(#${grainId})` }) +
    agedEdges(width, height, PAPER.aged) +
    boardFold(width, height, PAPER.shadow) +
    path(roundedRectPath(0, 0, width, height, PAPER_RADIUS), {
      fill: 'none',
      stroke: INK.key,
      'stroke-width': LINE.structure,
      opacity: 0.5,
    });

  return svg(width, height, head + sheet);
}

/**
 * The player's piece: a printed pawn, seen from the side. Board game pieces of
 * this era were a plastic pawn or a cardboard standee — a pawn reads at any size.
 */
export function renderPlayerToken(
  classId: 'warrior' | 'rogue' | 'mage' | 'priest',
  size = 72,
): string {
  const s = size / 72;
  const cx = size / 2;
  const ink = ROLE_INK[classId];
  const stroke = { stroke: INK.key, 'stroke-width': LINE.outline * s, 'stroke-linejoin': 'round' } as const;

  // Classic pawn: round head, waisted body, flared base.
  const pawn =
    `M${n(cx - 21 * s)} ${n(size * 0.94)}` +
    ` q${n(2 * s)} ${n(-9 * s)} ${n(9 * s)} ${n(-12 * s)}` +
    ` q${n(7 * s)} ${n(-4 * s)} ${n(5 * s)} ${n(-16 * s)}` +
    ` q${n(-2 * s)} ${n(-9 * s)} ${n(-1 * s)} ${n(-13 * s)}` +
    ` a${n(11 * s)} ${n(11 * s)} 0 1 1 ${n(16 * s)} 0` +
    ` q${n(1 * s)} ${n(4 * s)} ${n(-1 * s)} ${n(13 * s)}` +
    ` q${n(-2 * s)} ${n(12 * s)} ${n(5 * s)} ${n(16 * s)}` +
    ` q${n(7 * s)} ${n(3 * s)} ${n(9 * s)} ${n(12 * s)} Z`;

  const plate = misregistered(`pawn:${classId}`, size, MISREGISTRATION, path(pawn, { fill: ink }));
  const keyline = path(pawn, { fill: 'none', ...stroke });

  // A printed contact ellipse, not a blurred shadow — print has no blur.
  const contact = tag('ellipse', {
    cx,
    cy: size * 0.95,
    rx: 22 * s,
    ry: 4.5 * s,
    fill: INK.key,
    opacity: 0.18,
  });

  return svg(size, size, contact + plate + keyline);
}

/** Printed path between two spaces: a dashed track, the way boards mark routes. */
export function corridorDash(size = 10): string {
  return `${n(size)} ${n(size * 0.8)}`;
}

export const ROOM_STATES: readonly RoomState[] = [
  'fogged',
  'known',
  'visited',
  'cleared',
  'current',
  'reachable',
];
